export type CitizenFormConfiguration = {
  municipalityName: string;
  privacyUrl: string;
};

export type PublicCitizenForm = CitizenFormConfiguration & {
  scriptUrl: string;
  datasourceId: string;
  ingestionUrl: string;
};

// The public page adds "Comune di" itself: the stored name is the bare one (workspaces are often named "Comune di X").
export const bareMunicipalityName = (name: string) => name.trim().replace(/^comune\s+di\s+/i, '');

export const citizenFormUrl = (datasourceId: string) => `${window.location.origin}/iscrizione/${encodeURIComponent(datasourceId)}`;
const cookieName = (datasourceId: string) => `mdp_citizen_form_${encodeURIComponent(datasourceId)}`;

export const hasSubmittedCitizenForm = (datasourceId: string) =>
  document.cookie.split(';').some((cookie) => cookie.trim() === `${cookieName(datasourceId)}=submitted`);

export const markCitizenFormSubmitted = (datasourceId: string) => {
  document.cookie = `${cookieName(datasourceId)}=submitted; Path=/; Max-Age=31536000; SameSite=Lax${window.location.protocol === 'https:' ? '; Secure' : ''}`;
};

// Odd-position values for the codice fiscale check character, indexed by digit or letter (A=0 … Z=25).
const ODD_VALUES = [1, 0, 5, 7, 9, 13, 15, 17, 19, 21, 2, 4, 18, 20, 11, 3, 6, 8, 12, 14, 16, 10, 22, 25, 24, 23];
// Digits in positions 7-8, 10-11 and 13-15 may be replaced by LMNPQRSTUV (omocodia); month letters are ABCDEHLMPRST.
const FISCAL_CODE = /^[A-Z]{6}[0-9LMNPQRSTUV]{2}[ABCDEHLMPRST][0-9LMNPQRSTUV]{2}[A-Z][0-9LMNPQRSTUV]{3}[A-Z]$/;

export const isValidFiscalCode = (value: string) => {
  const code = value.trim().toUpperCase();
  if (!FISCAL_CODE.test(code)) return false;
  // birth day (positions 10-11, omocodia letters decoded) is 01-31 for men, 41-71 for women
  const day = Number([...code.slice(9, 11)].map((c) => (/\d/.test(c) ? c : 'LMNPQRSTUV'.indexOf(c))).join(''));
  if (!((day >= 1 && day <= 31) || (day >= 41 && day <= 71))) return false;
  const sum = [...code.slice(0, 15)].reduce((total, char, index) => {
    const n = /\d/.test(char) ? Number(char) : char.charCodeAt(0) - 65;
    return total + (index % 2 === 0 ? ODD_VALUES[n]! : n);
  }, 0);
  return code[15] === String.fromCharCode(65 + (sum % 26));
};

export const citizenTraits = (data: FormData, configuration: CitizenFormConfiguration) => ({
  name: String(data.get('name') ?? '').trim(),
  fiscal_code: String(data.get('fiscal_code') ?? '')
    .trim()
    .toUpperCase(),
  address_street: String(data.get('address_street') ?? '').trim(),
  phone: `+39${String(data.get('phone') ?? '').replace(/\D/g, '')}`,
  ...(String(data.get('email') ?? '').trim() ? { email: String(data.get('email')).trim() } : {}),
  pref_whatsapp: data.has('whatsapp'),
  pref_sms: data.has('sms'),
  pref_email: data.has('email_channel'),
  privacy_consent_at: new Date().toISOString(),
  privacy_url: configuration.privacyUrl,
});

type AnalyticsBundle = {
  supportsIdentifyResult?: boolean;
  create: (config: Record<string, unknown>) => { identify: (userId: string, traits: ReturnType<typeof citizenTraits>) => void };
};

declare global {
  interface Window {
    CDPAnalytics?: AnalyticsBundle;
  }
}

export const loadCitizenAnalytics = (scriptUrl: string): Promise<AnalyticsBundle> => {
  if (window.CDPAnalytics) return Promise.resolve(window.CDPAnalytics);
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = scriptUrl;
    script.async = true;
    script.onload = () => (window.CDPAnalytics ? resolve(window.CDPAnalytics) : reject(new Error('Analytics unavailable')));
    script.onerror = () => {
      script.remove();
      reject(new Error('Analytics unavailable'));
    };
    document.head.appendChild(script);
  });
};

const IDENTIFY_TIMEOUT_MS = 20_000;

export const identifyCitizen = (configuration: PublicCitizenForm, traits: ReturnType<typeof citizenTraits>) => {
  let timeout: ReturnType<typeof setTimeout>;
  let expired = false;
  // one deadline for script loading and delivery: a stalled CDN request or an SDK that never calls back must let the page offer a retry
  const deadline = new Promise<never>((_, reject) => {
    timeout = setTimeout(() => {
      expired = true;
      reject(new Error('Identify timed out'));
    }, IDENTIFY_TIMEOUT_MS);
  });
  const delivery = loadCitizenAnalytics(configuration.scriptUrl).then(
    (bundle) =>
      new Promise<void>((resolve, reject) => {
        // the page already reported failure: a late script load must not send the data behind the user's back
        if (expired) throw new Error('Identify timed out');
        if (!bundle.supportsIdentifyResult) throw new Error('Analytics needs updating');
        const analytics = bundle.create({
          apiUrl: configuration.ingestionUrl,
          datasourceId: configuration.datasourceId,
          enabled: true,
          persist: false,
          trackGoogleDataLayer: false,
          log: false,
          onIdentifyResult: (success: boolean) => (success ? resolve() : reject(new Error('Identify failed'))),
        });
        analytics.identify(traits.fiscal_code, traits);
      })
  );
  return Promise.race([delivery, deadline]).finally(() => clearTimeout(timeout));
};
